import { Dialog } from "@headlessui/react";
import type { AgencyPrintTheme } from "../../utils/agencyPrint";

type AgencyPrintOptionsModalCopy = Partial<{
  eyebrow: string;
  title: string;
  description: string;
  close: string;
  theme: string;
  themeLight: string;
  themeLightDescription: string;
  themeDark: string;
  themeDarkDescription: string;
  evidenceSelection: string;
  allEvidenceIncluded: string;
  selectedEvidence: (count: number) => string;
  deselectAll: string;
  selectAll: string;
  fileLabel: (index: number) => string;
  footer: string;
  downloadIdle: string;
  downloadBusy: string;
}>;

interface AgencyPrintOptionsModalProps {
  open: boolean;
  onClose: () => void;
  printTheme: AgencyPrintTheme;
  onThemeChange: (nextTheme: AgencyPrintTheme) => void;
  evidenceFiles: any[];
  selectedEvidenceIds: string[];
  onEvidenceToggle: (evidenceId: string) => void;
  onToggleAllEvidence: () => void;
  onDownloadPdf: () => void;
  pdfBusy?: boolean;
  copy?: AgencyPrintOptionsModalCopy;
}

const AgencyPrintOptionsModal = ({
  open,
  onClose,
  printTheme,
  onThemeChange,
  evidenceFiles,
  selectedEvidenceIds,
  onEvidenceToggle,
  onToggleAllEvidence,
  onDownloadPdf,
  pdfBusy = false,
  copy = {},
}: AgencyPrintOptionsModalProps) => {
  const printableEvidenceFiles = evidenceFiles.filter((file) => String(file?.id || "").trim());
  const allSelected =
    printableEvidenceFiles.length > 0 && selectedEvidenceIds.length >= printableEvidenceFiles.length;
  const hasSelectedEvidence = selectedEvidenceIds.length > 0;
  const resolvedCopy = {
    eyebrow: "Download PDF",
    title: "Prepare the case file",
    description: "Choose the paper theme and evidence set, then download the server-rendered PDF.",
    close: "Close",
    theme: "Theme",
    themeLight: "light",
    themeLightDescription: "Paper-first output with brighter cards and lower ink density.",
    themeDark: "dark",
    themeDarkDescription: "Dramatic dark packet for screen-first review and premium stock printing.",
    evidenceSelection: "Evidence Selection",
    allEvidenceIncluded: "All evidence files will be included.",
    selectedEvidence: (count: number) => `${count} evidence file(s) selected.`,
    deselectAll: "Deselect all",
    selectAll: "Select all",
    fileLabel: (index: number) => `File ${String(index + 1).padStart(2, "0")}`,
    footer: "PDF downloads use the dedicated print document renderer rather than the interactive case layout.",
    downloadIdle: "Download PDF",
    downloadBusy: "Preparing PDF...",
    ...copy,
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-[#030508]/75 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-3 sm:p-6">
          <Dialog.Panel className="w-full max-w-4xl overflow-hidden rounded-[28px] border border-white/12 bg-[radial-gradient(circle_at_top_left,rgba(179,152,96,0.16),transparent_28%),linear-gradient(180deg,#0a0f17,#0b1220)] text-white shadow-[0_35px_120px_rgba(0,0,0,0.55)]">
            <div className="border-b border-white/10 px-5 py-5 sm:px-7 sm:py-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.28em] text-[#d0c296]">{resolvedCopy.eyebrow}</p>
                  <Dialog.Title className="mt-2 text-2xl font-semibold text-white sm:text-3xl">
                    {resolvedCopy.title}
                  </Dialog.Title>
                  <Dialog.Description className="mt-2 max-w-2xl text-sm leading-6 text-white/68 sm:text-base">
                    {resolvedCopy.description}
                  </Dialog.Description>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white/75 transition hover:bg-white/[0.08] hover:text-white"
                >
                  {resolvedCopy.close}
                </button>
              </div>
            </div>

            <div className="grid gap-5 px-5 py-5 sm:px-7 sm:py-6 lg:grid-cols-[250px_minmax(0,1fr)]">
              <aside className="rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.035),rgba(255,255,255,0.02))] p-4">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#9f9a8e]">{resolvedCopy.theme}</p>
                <div className="mt-4 grid gap-2">
                  {(["light", "dark"] as AgencyPrintTheme[]).map((theme) => {
                    const active = printTheme === theme;
                    return (
                      <button
                        key={theme}
                        type="button"
                        onClick={() => onThemeChange(theme)}
                        className={`rounded-[20px] border px-4 py-4 text-left transition ${
                          active
                            ? "border-[#d0c296]/45 bg-[#d0c296]/12 shadow-[inset_0_0_0_1px_rgba(208,194,150,0.18)]"
                            : "border-white/10 bg-black/10 hover:border-white/20 hover:bg-white/[0.04]"
                        }`}
                      >
                        <p className="text-sm font-semibold capitalize text-white">
                          {theme === "light" ? resolvedCopy.themeLight : resolvedCopy.themeDark}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-white/60">
                          {theme === "light"
                            ? resolvedCopy.themeLightDescription
                            : resolvedCopy.themeDarkDescription}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </aside>

              <div className="rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.035),rgba(255,255,255,0.02))] p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#9f9a8e]">{resolvedCopy.evidenceSelection}</p>
                    <p className="mt-1 text-sm text-white/62">
                      {allSelected
                        ? resolvedCopy.allEvidenceIncluded
                        : resolvedCopy.selectedEvidence(selectedEvidenceIds.length)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onToggleAllEvidence}
                    className="rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-white/70 transition hover:bg-white/[0.06]"
                  >
                    {allSelected ? resolvedCopy.deselectAll : resolvedCopy.selectAll}
                  </button>
                </div>

                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {printableEvidenceFiles.map((file: any, index: number) => {
                    const evidenceId = String(file?.id || "").trim();
                    const active = selectedEvidenceIds.includes(evidenceId);
                    return (
                      <label
                        key={evidenceId}
                        className={`flex cursor-pointer items-start gap-3 rounded-[20px] border px-4 py-3 transition ${
                          active
                            ? "border-[#7ed0e3]/45 bg-[#7ed0e3]/10"
                            : "border-white/10 bg-black/10 hover:border-white/20 hover:bg-white/[0.04]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => onEvidenceToggle(evidenceId)}
                          className="mt-1 h-4 w-4 rounded border-white/20 bg-transparent text-[#7ed0e3] focus:ring-[#7ed0e3]/40"
                        />
                        <span className="min-w-0">
                          <span className="block text-[10px] uppercase tracking-[0.18em] text-[#9f9a8e]">
                            {resolvedCopy.fileLabel(index)}
                          </span>
                          <span className="mt-1 block text-sm font-semibold leading-5 text-white">
                            {String(file?.title || file?.type || `Evidence ${index + 1}`).trim()}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 bg-black/15 px-5 py-4 sm:px-7">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-white/58">
                  {resolvedCopy.footer}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={onDownloadPdf}
                    disabled={pdfBusy || !hasSelectedEvidence}
                    className="rounded-full border border-[#7ed0e3]/35 bg-[#7ed0e3]/10 px-4 py-2 text-sm font-semibold text-[#dff7fb] transition hover:bg-[#7ed0e3]/20 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {pdfBusy ? resolvedCopy.downloadBusy : resolvedCopy.downloadIdle}
                  </button>
                </div>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default AgencyPrintOptionsModal;
